import { AcrossDepositV3, AcrossSignatureType } from '@/types'

import { AbiCoder, AddressLike, BytesLike, keccak256, TypedDataDomain, verifyTypedData, Wallet } from 'ethers'

import { getSignatureType } from './get-signature-type'

const abiCoder = AbiCoder.defaultAbiCoder()

/******************************************************************************************/
/********************************* Used by Across Bridge **********************************/
/******************************************************************************************/

export const getAcrossOptimexAuthHash = (data: BytesLike): string => {
  const infoHash: string = keccak256(abiCoder.encode(['bytes'], [data]))
  return infoHash
}

export const getAcrossUserAuthHash = (refundAddress: string, toChain: [BytesLike, BytesLike, BytesLike]): string => {
  return keccak256(abiCoder.encode(['address', 'bytes[3]'], [refundAddress, toChain]))
}

export const getMPCAuthHash = (metadata: AcrossDepositV3): string => {
  const infoHash = keccak256(
    abiCoder.encode(
      ['tuple(address,address,address,address,uint256,uint256,uint256,address,uint32,uint32,uint32,bytes)'],
      [Object.values(metadata)]
    )
  )
  return infoHash
}

export const getBridgeAuthSigner = async (
  params: { tradeId?: BytesLike; infoHash: BytesLike },
  type: AcrossSignatureType,
  signature: string,
  domain: TypedDataDomain
): Promise<string> => {
  let values: any
  if (type === AcrossSignatureType.BridgeMPCAuth) values = { tradeId: params.tradeId, infoHash: params.infoHash }
  else values = { infoHash: params.infoHash }

  return verifyTypedData(domain, getSignatureType(type), values, signature)
}

export const getBridgeAuthSig = async (
  Signer: Wallet,
  params: { tradeId?: BytesLike; infoHash: BytesLike },
  type: AcrossSignatureType,
  domain: TypedDataDomain
): Promise<string> => {
  let values: any
  if (type === AcrossSignatureType.AcrossAgentAuth || type === AcrossSignatureType.FallbackTransfer) {
    values = { tradeId: params.tradeId, infoHash: params.infoHash }
  } else values = { infoHash: params.infoHash }
  return await Signer.signTypedData(domain, getSignatureType(type), values)
}

const getAcrossL2Value = (type: AcrossSignatureType, referenceId: BytesLike, infoHash?: BytesLike): any => {
  let values: any
  //  @dev used for AcrossRefund contract
  if (type === AcrossSignatureType.AcrossL2RequestRefund) values = { tradeId: referenceId, infoHash: infoHash }
  else if (type === AcrossSignatureType.AcrossL2ConfirmRefund) values = { requestId: referenceId, infoHash: infoHash }
  else if (type === AcrossSignatureType.AcrossL2RejectRefund) values = { requestId: referenceId, infoHash: infoHash }

  return values
}

export const getAcrossL2Signature = async ({
  Signer,
  tradeId,
  infoHash,
  type,
  domain,
}: {
  Signer: Wallet
  tradeId: BytesLike
  infoHash: BytesLike
  type: AcrossSignatureType
  domain: TypedDataDomain
}): Promise<string> => {
  const values = getAcrossL2Value(type, tradeId, infoHash)
  return await Signer.signTypedData(domain, getSignatureType(type), values)
}

export const getL2RefundRequestHash = ({
  networkId,
  tokenId,
  recipient,
  amount,
  expireTime,
}: {
  networkId: BytesLike
  tokenId: BytesLike
  recipient: AddressLike
  amount: bigint
  expireTime: bigint
}): string => {
  const infoHash: string = keccak256(
    abiCoder.encode(
      ['bytes', 'bytes', 'address', 'uint256', 'uint256'],
      [networkId, tokenId, recipient, amount, expireTime]
    )
  )

  return infoHash
}
